import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { LoginRequest, LoginResponse } from "../types";
import { config } from "../config";
import { loginRequest as msalLoginRequest } from "../lib/msalConfig";
import { reportFetchError, fetchWithTimeout } from "../lib/fetchError";

export function useAuth() {
  const { instance } = useMsal();
  const [loginResponse, setLoginResponse] = useState<LoginResponse | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  async function handleLogin(loginRequest: LoginRequest) {
    setIsLoading(true);
    setError(null);

    const url = `${config.apiUrl}/add-in/auth/login`;
    try {
      const response = await fetchWithTimeout(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: loginRequest.email,
          password: loginRequest.password,
          language: loginRequest.language ?? "nl-NL",
        }),
      });

      if (!response.ok) {
        throw new Error("Inloggen is mislukt. Controleer je e-mailadres en wachtwoord.");
      }

      const data: LoginResponse = await response.json();
      setLoginResponse(data);
      setEmail(loginRequest.email);
      setError(null);
    } catch (e) {
      setError(reportFetchError(e, url));
      setLoginResponse(null);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleMicrosoftLogin() {
    setIsLoading(true);
    setError(null);

    // In Office WebView2, the popup may run in a separate process where BroadcastChannel
    // doesn't reach the taskpane. The popup sends window.opener.postMessage as a fallback.
    // We receive it here and relay it to BroadcastChannel within this same process,
    // where MSAL's waitForBridgeResponse listener is already running.
    function relayPopupMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin || event.data?.type !== "msal-popup-relay")
        return;
      const { payload, stateId } = event.data as {
        payload: string;
        stateId: string;
      };
      const channel = new BroadcastChannel(stateId);
      channel.postMessage({ payload, v: 1 });
      channel.close();
    }
    window.addEventListener("message", relayPopupMessage);

    const url = `${config.apiUrl}/add-in/auth/microsoft`;
    try {
      const result = await instance.loginPopup(msalLoginRequest);

      const response = await fetchWithTimeout(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ token: result.idToken }),
      });

      if (response.status === 404) {
        setError("Geen account gevonden voor dit Microsoft account.");
        return;
      }

      if (!response.ok) {
        throw new Error("Microsoft inloggen is mislukt");
      }

      const data: LoginResponse = await response.json();
      setLoginResponse(data);
      setEmail(result.account?.username ?? null);
      setError(null);
    } catch (e: any) {
      if (e?.errorCode === "user_cancelled") {
        // User closed the popup — no error needed
      } else {
        setError(reportFetchError(e, url));
      }
      setLoginResponse(null);
    } finally {
      window.removeEventListener("message", relayPopupMessage);
      setIsLoading(false);
    }
  }

  return {
    loginResponse,
    email,
    error,
    handleLogin,
    handleMicrosoftLogin,
    isLoading,
  };
}
